import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { UnifiedCard } from "@/components/cards/UnifiedCard";
import { Badge } from "@/components/ui/badge";
import { Globe, ShieldCheck, Database, Users, Plus, Trash2, RotateCcw, Play } from "lucide-react";
import { WorldCardActions } from "@/components/cards/WorldCardActions";
import { ConfirmDeleteDouble } from "@/components/ui/confirm-delete-double";
import { useLocale } from "@/hooks/useLocale";
import { fmtDate } from "@/lib/locale-format";
import { getLocalizedWorldName, getLocalizedWorldDescription } from "@/lib/world-i18n";
import type { Translations } from "@/lib/i18n/types";
import type { PublicWorldItem, WorldItem } from "@/hooks/useIndexData";
import { WorldStatsRow } from "./WorldStatsRow";

// ============ PUBLIC WORLDS TAB ============

export function PublicWorldsTab({
  t, publicWorlds, cloningWorldId, navigate, onClone, setShowNewGame, setSelectedWorldId, worldStats,
}: {
  t: Translations;
  publicWorlds: PublicWorldItem[];
  cloningWorldId: string | null;
  navigate: (path: string) => void;
  onClone: (worldId: string, worldName: string) => Promise<void>;
  setShowNewGame: (v: boolean) => void;
  setSelectedWorldId: (v: string) => void;
  worldStats: Record<string, { entities: number; characters: number; scenarios: number; tags: string[] }>;
}) {
  const { locale } = useLocale();
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Globe className="w-5 h-5 text-[hsl(var(--candleglow))]" />
          {t.idx_public_worlds}
          <Badge variant="secondary">{publicWorlds.length}</Badge>
        </h2>
      </div>
      {publicWorlds.length === 0 ? (
        <Card className="p-12 text-center space-y-4">
          <Globe className="w-12 h-12 mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground">{t.idx_no_public_worlds}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {publicWorlds.filter((w: any) => !w.is_confidential).map((world) => (
            <UnifiedCard
              key={world.id}
              id={world.id}
              cardType="public"
              onClick={() => navigate(`/world/${world.id}`)}
              bannerUrl={world.banner_url}
              icon={world.icon}
              title={getLocalizedWorldName(world, locale)}
              description={getLocalizedWorldDescription(world, locale)}
              extraContent={<WorldStatsRow stats={worldStats[world.id]} />}
              badges={world.is_own ? <Badge className="text-[10px] bg-primary text-primary-foreground">{t.idx_yours}</Badge> : undefined}
              actions={
                <WorldCardActions
                  worldId={world.id}
                  worldName={world.name}
                  role={world.is_own ? "owner" : "public"}
                  allowCloning={world.allow_cloning}
                  isPublic
                  onView={() => navigate(`/world/${world.id}`)}
                  onEdit={world.is_own ? () => navigate(`/world/${world.id}`) : undefined}
                  onPlay={() => { setSelectedWorldId(world.id); setShowNewGame(true); }}
                  onDuplicate={world.allow_cloning || world.is_own ? () => onClone(world.id, world.name) : undefined}
                />
              }
              footer={<p className="text-xs text-muted-foreground">by {world.owner_name}</p>}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============ TRASH TAB ============

export function TrashTab({
  t, trashedWorlds, onRestore, onPermanentDelete, navigate,
}: {
  t: Translations;
  trashedWorlds: WorldItem[];
  onRestore: (worldId: string) => Promise<void>;
  onPermanentDelete: (worldId: string) => Promise<void>;
  navigate: (path: string) => void;
}) {
  const { locale } = useLocale();
  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return fmtDate(new Date(dateStr), locale);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-destructive" />
          {t.idx_trashed_worlds}
          <Badge variant="secondary">{trashedWorlds.length}</Badge>
        </h2>
      </div>
      <p className="text-sm text-muted-foreground">{t.idx_trash_desc}</p>
      {trashedWorlds.length === 0 ? (
        <Card className="p-12 text-center space-y-4">
          <Trash2 className="w-12 h-12 mx-auto text-muted-foreground/30" />
          <p className="text-muted-foreground">{t.idx_trash_empty}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trashedWorlds.map((world: any) => (
            <UnifiedCard
              key={world.id}
              id={world.id}
              cardType="trash"
              grayscale
              bannerUrl={world.banner_url}
              icon={world.icon}
              title={getLocalizedWorldName(world, locale)}
              description={getLocalizedWorldDescription(world, locale)}
              footer={<div className="text-xs text-muted-foreground">{t.idx_deleted} {formatTimeAgo(world.deleted_at)}</div>}
              bottomActions={
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs flex-1" onClick={() => onRestore(world.id)}>
                    <RotateCcw className="w-3.5 h-3.5" />{t.idx_restore}
                  </Button>
                  <ConfirmDeleteDouble
                    onConfirm={() => onPermanentDelete(world.id)}
                    title={t.idx_delete_forever_title}
                    description={`"${world.name}" ${t.idx_delete_forever_desc}`}
                    confirmText={world.name}
                    trigger={
                      <Button variant="destructive" size="sm" className="gap-1.5 text-xs flex-1">
                        <Trash2 className="w-3.5 h-3.5" />{t.idx_delete_forever}
                      </Button>
                    }
                  />
                </div>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============ ADMIN ALL WORLDS TAB ============

export function AdminWorldsTab({
  allWorlds, navigate, worldStats, setShowNewGame, setSelectedWorldId, onClone, cloningWorldId,
}: {
  allWorlds: PublicWorldItem[];
  navigate: (path: string) => void;
  worldStats: Record<string, { entities: number; characters: number; scenarios: number; tags: string[] }>;
  setShowNewGame: (v: boolean) => void;
  setSelectedWorldId: (v: string) => void;
  onClone: (worldId: string, worldName: string) => Promise<void>;
  cloningWorldId: string | null;
}) {
  const { locale, t } = useLocale();
  const [search, setSearch] = useState("");
  const filtered = allWorlds.filter(
    (w) =>
      w.name.toLowerCase().includes(search.toLowerCase()) ||
      (w.owner_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (w.description || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-destructive" />
          {t.idx_all_worlds_admin}
          <Badge variant="secondary">{allWorlds.length}</Badge>
        </h2>
        <Input placeholder="Search worlds or owners..." value={search} onChange={(e: any) => setSearch(e.target.value)} className="max-w-xs" />
      </div>
      {filtered.length === 0 ? (
        <Card className="p-12 text-center space-y-4">
          <Database className="w-12 h-12 mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground">No worlds found</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((world) => (
            <UnifiedCard
              key={world.id}
              id={world.id}
              cardType="public"
              onClick={() => navigate(`/world/${world.id}`)}
              bannerUrl={world.banner_url}
              icon={world.icon}
              title={getLocalizedWorldName(world, locale)}
              description={getLocalizedWorldDescription(world, locale)}
              extraContent={<WorldStatsRow stats={worldStats[world.id]} />}
              badges={
                <div className="flex items-center gap-1">
                  {world.is_own && <Badge className="text-[10px] bg-primary text-primary-foreground">Yours</Badge>}
                  {world.is_public && <Badge variant="outline" className="text-[10px]">Public</Badge>}
                </div>
              }
              actions={
                <WorldCardActions
                  worldId={world.id}
                  worldName={world.name}
                  role={world.is_own ? "owner" : "public"}
                  allowCloning
                  isPublic={!!world.is_public}
                  onView={() => navigate(`/world/${world.id}`)}
                  onPlay={() => { setSelectedWorldId(world.id); setShowNewGame(true); }}
                  onDuplicate={() => onClone(world.id, world.name)}
                />
              }
              footer={
                <div className="flex items-center justify-between w-full">
                  <p className="text-xs text-muted-foreground">by {world.owner_name}</p>
                  <p className="text-[10px] text-muted-foreground/60 font-mono">{world.id.slice(0, 8)}</p>
                </div>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
